/**
 * Intent Selector Component
 *
 * Customer-focused entry point that determines the appropriate flow path
 * based on what the customer actually wants to accomplish.
 */

import { cn } from '../../lib/utils';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { TruckIcon, Package2Icon } from 'lucide-react';
import { SocialProofBadge, type ProofType } from '../social/SocialProofBadge';

export interface IntentOption {
  id: 'new_delivery' | 'existing_container';
  icon: React.ReactNode;
  title: string;
  description: string;
  recommended?: boolean;
  socialProof?: {
    type: ProofType;
    value?: string | number;
  };
}

interface IntentSelectorProps {
  options?: IntentOption[];
  selectedIntent: string | null;
  onSelect: (id: string) => void;
}

const DEFAULT_OPTIONS: IntentOption[] = [
  {
    id: 'new_delivery',
    icon: <TruckIcon className="w-8 h-8" />,
    title: 'I need a container delivered',
    description: 'Get an empty container delivered to your location',
  },
  {
    id: 'existing_container',
    icon: <Package2Icon className="w-8 h-8" />,
    title: 'I have a container on-site',
    description: 'Swap out or pick up your existing container',
  },
];

export function IntentSelector({
  options = DEFAULT_OPTIONS,
  selectedIntent,
  onSelect,
}: IntentSelectorProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          What can we help you with today?
        </h2>
        <p className="text-sm text-gray-600">
          Choose the option that best describes what you need
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {options.map((option) => (
          <Card
            key={option.id}
            className={cn(
              'relative cursor-pointer transition-all duration-200',
              'hover:shadow-lg hover:scale-[1.02]',
              'focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2',
              selectedIntent === option.id && 'ring-2 ring-primary shadow-lg'
            )}
            onClick={() => onSelect(option.id)}
          >
            {/* Social Proof Badge */}
            {option.socialProof && (
              <div className="absolute -top-2 -right-2 z-10">
                <SocialProofBadge
                  type={option.socialProof.type}
                  value={option.socialProof.value}
                  size="sm"
                  variant="prominent"
                />
              </div>
            )}

            {/* Fallback to recommended badge if no social proof */}
            {!option.socialProof && option.recommended && (
              <Badge
                className="absolute -top-2 -right-2 z-10"
                variant="default"
              >
                Most Common
              </Badge>
            )}

            <CardContent className="p-6">
              <button
                type="button"
                className="w-full min-h-[88px] text-left focus:outline-none"
                aria-pressed={selectedIntent === option.id}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 text-primary">
                    {option.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {option.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {option.description}
                    </p>
                  </div>
                </div>
              </button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Helper text for existing customers */}
      <div className="text-center text-sm text-gray-500">
        <p>
          Already have an account?{' '}
          <button
            type="button"
            className="inline-flex items-center justify-center min-h-[44px] px-2 text-primary hover:underline focus:outline-none focus:underline"
            onClick={() => {
              // This would trigger login modal or redirect
              console.log('Show login');
            }}
          >
            Sign in for faster checkout
          </button>
        </p>
      </div>
    </div>
  );
}